import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useRecordTable } from '../../../hooks/useRecordTable';
import UniversalTableHeader from './UniversalTableHeader';
import UniversalTableBody from './UniversalTableBody';
import UniversalTablePagination from './UniversalTablePagination';
import SvgIcon from '../../SvgIcon/SvgIcon';
import FilterBar, { FilterConfig } from '../advancedRecord/FilterBar';
import { CustomColumn } from '../../../types/customColumn';


interface TopLeftAction {
  label: string;
  onClick: () => void;
  count?: number;
}

interface TopRightAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

interface UniversalRecordTableProps<T extends object> {
  columns: CustomColumn<T>[];
  data: T[];
  filtersConfig?: FilterConfig[];
  onFilterChange?: (filters: Record<string, any>) => void;
  onResetFilters?: () => void;
  showFilters?: boolean;
  pageSize?: number;
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void;
  paginator?: any;
  getActions?: (row: T) => any[];
  noDataMessage?: string;
  maxWidth?: string;
  topLeftActions?: TopLeftAction[];
  topRightAction?: TopRightAction;
  showPagination?: boolean; // 👈 nuevo
  enableRowClick?: boolean; // 👈 nuevo
}

const UniversalRecordTable = <T extends object>({
  columns,
  data,
  filtersConfig,
  onFilterChange,
  onResetFilters,
  pageSize = 10,
  onPageSizeChange,
  onPageChange,
  paginator,
  getActions,
  noDataMessage = 'No hay registros disponibles.',
  maxWidth = 'w-full',
  topLeftActions,
  topRightAction,
  showPagination,
  showFilters = false,
  enableRowClick,
}: UniversalRecordTableProps<T>) => {
  const table = useRecordTable<T>({ columns, data, pageSize });

  const hasTopLeft = topLeftActions && topLeftActions.length > 0;
  const hasTopRight =
    topRightAction && (topRightAction.onClick || topRightAction.href);
  const hasFilters = filtersConfig && filtersConfig.length > 0;

  return (
    <div
      className={clsx(
        'mt-6 w-full rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark',
        maxWidth
      )}
    >
      {/* Acciones superiores */}
      {(hasTopLeft || hasTopRight) && (
        <div
          className={clsx(
            'gap-2 px-4 pt-4',
            hasTopLeft && hasTopRight
              ? 'flex flex-col sm:flex-row sm:items-center sm:justify-between'
              : hasTopLeft
              ? 'flex justify-start'
              : 'flex justify-end'
          )}
        >
          {/* Izquierda */}
          {hasTopLeft && (
            <div className="flex flex-wrap gap-2">
              {topLeftActions!.map((btn, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={btn.onClick}
                  className="flex items-center justify-center gap-2 rounded bg-primary px-4.5 py-2.5 text-sm font-medium text-white hover:bg-opacity-90"
                >
                  <span>{btn.label}</span>
                  {btn.count !== undefined && (
                    <span className="ml-2 rounded-full bg-white bg-opacity-20 px-2 py-0.5 text-xs font-semibold">
                      {btn.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Derecha */}
          {hasTopRight &&
            (topRightAction!.onClick ? (
              <button
                type="button"
                onClick={topRightAction!.onClick}
                className="flex items-center gap-2 rounded bg-primary px-4.5 py-2.5 text-sm font-medium text-white hover:bg-opacity-90"
              >
                <SvgIcon name="plus" className="h-4 w-4" />
                {topRightAction!.label}
              </button>
            ) : (
              <Link
                to={topRightAction!.href!}
                className="flex items-center gap-2 rounded bg-primary px-4.5 py-2.5 text-sm font-medium text-white hover:bg-opacity-90"
              >
                <SvgIcon name="plus" className="h-4 w-4" />
                {topRightAction!.label}
              </Link>
            ))}
        </div>
      )}

      {/* Filtros (solo si hay configuración) */}
      {hasFilters && (
        <FilterBar
          filtersConfig={filtersConfig!}
          onFilterChange={onFilterChange}
          onResetFilters={onResetFilters}
        />
      )}

      {/* Tabla principal */}
      <section
        className={clsx(
          'data-table-common border-y border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark',
          (hasTopLeft || hasTopRight || hasFilters) && 'mt-6'
        )}
      >
        <div className="w-full overflow-x-auto p-2">
          <table
            {...table.getTableProps()}
            className="datatable-table w-full min-w-[700px] table-auto border-collapse break-words md:table-fixed md:overflow-auto"
          >
            <UniversalTableHeader table={table} showFilters={showFilters} />
            <UniversalTableBody
              table={table}
              getActions={getActions}
              noDataMessage={noDataMessage}
              enableRowClick={enableRowClick}
            />
          </table>
        </div>

        {showPagination && (
          <UniversalTablePagination
            data={data}
            paginator={paginator}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        )}
      </section>
    </div>
  );
};

export default UniversalRecordTable;
